import CryptoJS from 'crypto-js';
import { PBX_PROXY_URL, PBX_DIRECT_URL, PBX_HOST } from '../../utils/ApiRoute';

const GRANDSTREAM_DIRECT_URL = PBX_DIRECT_URL;
const GRANDSTREAM_HOST = PBX_HOST;

/**
 * Universal Post Helper.
 * Handles CORS and Private Network restrictions by routing through Backend Proxy URL
 * on public origins (e.g. epmsystem.qcodesinfotech.com) with direct fallback for local intranets.
 */
async function sendPbxPost(requestPayload) {
  // Always use Backend Proxy URL to bypass browser CORS & SSL blocks
  if (PBX_PROXY_URL) {
    try {
      const headers = { 'Content-Type': 'application/json' };
      const authToken = localStorage.getItem("AuthToken");
      const tokenStr = localStorage.getItem("token");

      let bearer = "";
      if (authToken) {
        try { bearer = JSON.parse(authToken); } catch (e) { bearer = authToken; }
      } else if (tokenStr) {
        try {
          const parsed = JSON.parse(tokenStr);
          bearer = parsed?.token || parsed?.access || parsed?.jwt || (typeof parsed === 'string' ? parsed : "");
        } catch (e) {
          bearer = tokenStr;
        }
      }

      if (bearer) {
        headers['Authorization'] = `Bearer ${bearer}`;
      }

      const proxyRes = await fetch(PBX_PROXY_URL, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          target_url: GRANDSTREAM_DIRECT_URL,
          request: requestPayload
        })
      });
      if (proxyRes.ok) {
        return await proxyRes.json();
      }
    } catch (proxyErr) {
      console.warn("Backend PBX proxy notice:", proxyErr.message);
    }
  }

  // 2. Direct HTTPS fetch fallback for local intranet / dev server
  const directRes = await fetch(GRANDSTREAM_DIRECT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ request: requestPayload })
  });

  return await directRes.json();
}

/**
 * Grandstream UCM6308 API Client.
 * Manages full authentication flow (challenge -> MD5 token login -> cookie storage -> authenticated actions).
 */
export const grandstreamClient = {
  loginWithCredentials: async (user, pass) => {
    try {
      const username = user || localStorage.getItem('pbx_user') || 'cdrapi';
      const password = pass || localStorage.getItem('pbx_pass') || 'cdrapi123';

      // 1. Challenge Step
      const challengeData = await sendPbxPost({
        action: 'challenge',
        user: username,
        username: username
      });

      const challengeToken = challengeData?.response?.challenge || challengeData?.response?.token;

      if (!challengeToken) {
        throw new Error(challengeData?.response?.error_msg || "Challenge request failed");
      }

      // Hash Calculations for UCM6300 Series API
      const passMd5 = CryptoJS.MD5(password).toString();

      const hashCombos = [
        CryptoJS.MD5(challengeToken + password).toString(),  // PRIMARY: MD5(challenge + password)
        CryptoJS.MD5(password + challengeToken).toString(),  // MD5(password + challenge)
        CryptoJS.MD5(challengeToken + passMd5).toString(),   // MD5(challenge + MD5(password))
        CryptoJS.MD5(passMd5 + challengeToken).toString()    // MD5(MD5(password) + challenge)
      ];

      let loginData = null;

      for (const tokenHash of hashCombos) {
        loginData = await sendPbxPost({
          action: 'login',
          user: username,
          username: username,
          token: tokenHash,
          url: GRANDSTREAM_HOST
        });

        const cookieVal = loginData?.response?.cookie || loginData?.response?.body?.cookie || loginData?.cookie;

        if (loginData?.status === 0 && cookieVal) {
          localStorage.setItem('pbx_cookie', cookieVal);
          localStorage.setItem('pbx_unlocked', 'true');
          localStorage.setItem('pbx_user', username);
          localStorage.setItem('pbx_pass', password);
          sessionStorage.setItem('pbx_unlocked', 'true');
          console.log("Grandstream PBX Cookie stored in localStorage:", cookieVal);
          return cookieVal;
        }
      }

      throw new Error(loginData?.response?.error_msg || "Wrong account or password!");
    } catch (err) {
      console.warn("Grandstream authentication notice:", err.message);
      throw err;
    }
  },

  login: async () => {
    try {
      return await grandstreamClient.loginWithCredentials();
    } catch (e) {
      return null;
    }
  },

  sendAction: async (actionName, params = {}) => {
    try {
      let activeCookie = localStorage.getItem('pbx_cookie');

      if (!activeCookie) {
        activeCookie = await grandstreamClient.login();
      }

      if (!activeCookie) {
        return null;
      }

      const payload = {
        action: actionName,
        cookie: activeCookie,
        ...params
      };

      const data = await sendPbxPost(payload);

      if (
        data?.status === -8 ||
        data?.status === -1 ||
        data?.status === -6 ||
        data?.status === -47 ||
        data?.status === 401 ||
        data?.response?.error_msg?.toLowerCase()?.includes("timeout") ||
        data?.response?.error_msg?.toLowerCase()?.includes("permission")
      ) {
        localStorage.removeItem('pbx_cookie');
        activeCookie = await grandstreamClient.login();
        if (activeCookie) {
          payload.cookie = activeCookie;
          return await sendPbxPost(payload);
        }
      }

      return data;
    } catch (err) {
      console.warn(`Grandstream API action [${actionName}] failed:`, err.message);
      return null;
    }
  }
};

export default grandstreamClient;
